import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { 
  Building2, 
  Users, 
  GraduationCap, 
  BookOpen, 
  Settings,
  Eye
} from 'lucide-react';
import dashboardService, { type DashboardStats } from '../../services/dashboardService';
import StatsCard from './components/StatsCard';
import ActivityItem from './components/ActivityItem';
import DetailedStats from './components/DetailedStats';
import SystemAlerts from './components/SystemAlerts';
import QuickStats from './components/QuickStats';
import { ADMIN_PATHS } from './adminPaths';

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await dashboardService.getDashboardStats();
        setStats(data);
        setError(null);
      } catch (err) {
        setError('Không thể tải dữ liệu dashboard');
        console.error('Failed to fetch dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);



  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="text-red-400">
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Lỗi khi tải dữ liệu</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tổng quan Hệ thống</h1>
          <p className="text-gray-600 mt-1">Quản lý toàn diện trường học, lớp học, giáo viên và học sinh</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center">
            <Settings className="h-4 w-4 mr-2" />
            Cài đặt
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Trường học"
          value={stats.schools.total}
          active={stats.schools.active}
          total={stats.schools.total}
          icon={Building2}
          color="text-blue-600"
          bgColor="bg-blue-100"
        />
        <StatsCard
          title="Lớp học"
          value={stats.classes.total}
          active={stats.classes.active}
          total={stats.classes.total}
          icon={BookOpen}
          color="text-green-600"
          bgColor="bg-green-100"
        />
        <StatsCard
          title="Giáo viên"
          value={stats.teachers.total}
          active={stats.teachers.active}
          total={stats.teachers.total}
          icon={GraduationCap}
          color="text-purple-600"
          bgColor="bg-purple-100"
        />
        <StatsCard
          title="Học sinh"
          value={stats.students.total}
          active={stats.students.active}
          total={stats.students.total}
          icon={Users}
          color="text-orange-600"
          bgColor="bg-orange-100"
          formatValue={(value) => value.toLocaleString()}
        />
      </div>

      {/* Detailed Stats */}
      <DetailedStats stats={stats} />

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* School Distribution Chart */}
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Phân bố loại hình trường học</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={stats.schoolDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percentage }) => `${name}: ${percentage}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {stats.schoolDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Class Distribution Chart */}
        <div className="bg-white p-6 rounded-xl shadow-sm border">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Phân bố lớp học theo khối</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={stats.classDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="grade" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3B82F6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Stats Chart */}
      <div className="bg-white p-6 rounded-xl shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Thống kê theo tháng</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={stats.monthlyStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="schools" stroke="#3B82F6" strokeWidth={2} name="Trường học" />
            <Line type="monotone" dataKey="classes" stroke="#10B981" strokeWidth={2} name="Lớp học" />
            <Line type="monotone" dataKey="teachers" stroke="#8B5CF6" strokeWidth={2} name="Giáo viên" />
            <Line type="monotone" dataKey="students" stroke="#F59E0B" strokeWidth={2} name="Học sinh" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Quick Stats */}
      <QuickStats monthlyStats={stats.monthlyStats ?? []} />

      {/* System Alerts */}
      <SystemAlerts />

      {/* Quick Actions and Recent Activities */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Thao tác nhanh</h2>
          <div className="grid grid-cols-1 gap-3">
            <Link 
              to={ADMIN_PATHS.school}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <div className="p-2 bg-blue-100 rounded-lg mr-3 group-hover:bg-blue-200 transition-colors">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-medium text-gray-900">Quản lý Trường học</p>
                <p className="text-sm text-gray-500">Xem và quản lý danh sách trường học</p>
              </div>
              <Eye className="h-4 w-4 text-gray-400" />
            </Link>

            <Link 
              to={ADMIN_PATHS.class}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <div className="p-2 bg-green-100 rounded-lg mr-3 group-hover:bg-green-200 transition-colors">
                <BookOpen className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-medium text-gray-900">Quản lý Lớp học</p>
                <p className="text-sm text-gray-500">Tạo và quản lý lớp học</p>
              </div>
              <Eye className="h-4 w-4 text-gray-400" />
            </Link>

            <Link 
              to={ADMIN_PATHS.schoolTeachers}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <div className="p-2 bg-purple-100 rounded-lg mr-3 group-hover:bg-purple-200 transition-colors">
                <GraduationCap className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-medium text-gray-900">Quản lý Giáo viên</p>
                <p className="text-sm text-gray-500">Quản lý thông tin giáo viên</p>
              </div>
              <Eye className="h-4 w-4 text-gray-400" />
            </Link>

            <Link 
              to={ADMIN_PATHS.schoolStudents}
              className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <div className="p-2 bg-orange-100 rounded-lg mr-3 group-hover:bg-orange-200 transition-colors">
                <Users className="h-5 w-5 text-orange-600" />
              </div>
              <div className="text-left flex-1">
                <p className="font-medium text-gray-900">Quản lý Học sinh</p>
                <p className="text-sm text-gray-500">Quản lý thông tin học sinh</p>
              </div>
              <Eye className="h-4 w-4 text-gray-400" />
            </Link>
          </div>
        </div>

        {/* Recent Activities */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Hoạt động gần đây</h2>
          <div className="space-y-4">
            {stats.recentActivities.map((activity) => (
              <ActivityItem key={activity.id} activity={activity} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 